import { assertConstantValue } from '@watershed/lscript/assert';
import { ASTCall } from '@watershed/lscript/ast';
import { ExprType, makeNullable } from '@watershed/lscript/expr_types';
import assertNever from '@watershed/shared-util/src/assertNever';

import { joinTickedWithAnd } from '../../utils/helpers';
import { extractStringLiteral } from '../literal';
import { maybeAddInputVariable, traceHelper } from '../sql/internal';
import { TraceASTInput, TraceASTType } from '../trace';
import {
  assertArgShape,
  assertKwargShape,
  TypecheckWarning,
  typecheckExpression,
} from '../typecheck';
import { Context, Func, SqlExpression } from '../types';

// Function for looking up a column in the input type.
//
// This function is similar to the bart function, but it returns a nullable
// type. This is useful for cases where the column may not be present in the
// input. For example, we cannot guarantee that a custom column will be present
// in the input, so we need to return a Nullable<type> for the custom column.
export const inputSilent: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 1, 1);
    assertKwargShape(call, [], false, warnings, undefined);

    // It's important that it's a literal, since we use static analysis to
    // figure out which input columns a given expression uses.
    const field = extractStringLiteral(call.args[0]);

    const columnType = ctx.inputType.get(field);

    // if the field does not exist, we'll assume it's a nullable string and add
    // a warning
    if (!columnType) {
      const known = joinTickedWithAnd(ctx.inputType.keys());
      warnings?.push(
        `Unknown BART column \`${field}\` (know about ${known}): assuming type 'string'.`
      );
      return makeNullable({ ty: 'string' });
    }

    // This looks useless, but we're dropping an extra "column" field from
    // RowType
    if (columnType.ty === 'quantity') {
      return makeNullable({ ty: 'quantity', units: columnType.units });
    }

    return makeNullable(columnType);
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const field = extractStringLiteral(call.args[0]);

    const v = ctx.input.get(field);
    if (v !== undefined && v !== null) {
      return assertConstantValue(v);
    }

    return null;
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const ty = typecheckExpression(ctx, call);
    const name = extractStringLiteral(call.args[0]);

    const duckDbType = exprTypeToDuckDbType(ty);
    const columnSql = maybeAddInputVariable(ctx, name, duckDbType);

    return traceHelper<TraceASTInput>(
      ty,
      {
        expr: columnSql,
        stage: 0,
        unitExpr: null,
        error: null,
      },
      () => ({
        t: TraceASTType.TInput,
        c: name,
      })
    );
  },
};

/**
 * Maps ExprType to DuckDB SQL type for json_transform operations. Handles
 * primitive types, nullable types, and falls back to VARCHAR for complex types,
 * but those aren't allowed as return types for column function anyway.
 */
function exprTypeToDuckDbType(ty: ExprType): string {
  switch (ty.ty) {
    case 'string':
      return 'VARCHAR';
    case 'boolean':
      return 'BOOLEAN';
    case 'number':
    case 'quantity':
      return 'DOUBLE';
    case 'null':
      return 'VARCHAR'; // Default to VARCHAR for null
    case 'nullable':
      return exprTypeToDuckDbType(ty.baseTy);
    case 'list':
      // For lists, we'll use VARCHAR as a fallback since JSON arrays are
      // complex
      return 'VARCHAR';
    case 'date':
      return 'DATE';
    case 'datetime':
      return 'TIMESTAMP';
    default:
      assertNever(ty);
  }
}

export const exportedForTesting = {
  exprTypeToDuckDbType,
};
